import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useTestimonials, useSettings } from "../hooks/useCms";
import { resolveImage } from "../data/site";
import PageHeader from "../components/PageHeader";
import { FadeUp, Stagger, StaggerItem } from "../components/motion";
import SEO from "../components/SEO";

const Diamonds = ({ count, size = "w-2 h-2" }) => (
  <span className="inline-flex items-center gap-1.5" aria-label={`${count} out of 5`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`${size} rotate-45 ${i < count ? "bg-[#8C6A4F]" : "bg-[#1A1A1A]/15"}`} />
    ))}
  </span>
);

const RatingSummary = ({ testimonials }) => {
  const rated = testimonials.filter((t) => t.rating > 0);
  const avg = rated.length ? rated.reduce((s, t) => s + t.rating, 0) / rated.length : null;
  return (
    <FadeUp className="border border-[rgba(26,26,26,0.15)] bg-white/60 p-10 md:p-14" data-testid="rating-summary">
      <p className="eyebrow text-[#8C6A4F] mb-8">Homeowner Rating</p>
      {avg !== null ? (
        <div className="flex items-end gap-4">
          <span className="font-serif text-7xl md:text-8xl leading-none tracking-tight" data-testid="average-rating">
            {avg.toFixed(1)}
          </span>
          <span className="font-mono text-sm text-[#A3A09B] pb-3">/ 5</span>
        </div>
      ) : (
        <p className="font-serif text-3xl md:text-4xl leading-tight" data-testid="average-rating-none">
          Every build, personally delivered.
        </p>
      )}
      {avg !== null && <div className="mt-6"><Diamonds count={Math.round(avg)} size="w-2.5 h-2.5" /></div>}
      <p className="mt-6 font-mono text-[0.68rem] tracking-[0.2em] uppercase text-[#5C5A56]" data-testid="review-count">
        {testimonials.length} homeowner {testimonials.length === 1 ? "review" : "reviews"}
      </p>
    </FadeUp>
  );
};

const Reviews = () => {
  const testimonials = useTestimonials();
  const settings = useSettings();
  const sorted = [...testimonials].sort((a, b) => (b.featured ? 1 : 0) - (a.featured ? 1 : 0));

  return (
    <main>
      <SEO
        title="Client Reviews | On Point Homes | Idaho Home Builder"
        description="Read reviews from Idaho homeowners who built with On Point Homes — honest stories about a personal, hands-on building experience."
      />
      <PageHeader
        testid="reviews-header"
        eyebrow="Client Stories"
        lines={[{ text: "Words From" }, { text: "Our Homeowners", italic: true }]}
        description="Every home we build ends with a relationship, not a handshake. Here's what the people who live in our homes have to say — in their own words."
      />

      <section className="pb-28 md:pb-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10">
          {sorted.length > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16">
                <div className="lg:col-span-5">
                  <RatingSummary testimonials={sorted} />
                </div>
                <div className="lg:col-span-6 lg:col-start-7 flex items-end">
                  <FadeUp delay={0.15}>
                    <p className="font-serif italic text-2xl md:text-3xl text-[#5C5A56] leading-snug">
                      "We intentionally stay small — so every one of these stories comes from a home we know
                      board by board."
                    </p>
                  </FadeUp>
                </div>
              </div>

              <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="reviews-grid">
                {sorted.map((t) => (
                  <StaggerItem key={t.id}>
                    <figure className="h-full border-l-2 border-[#8C6A4F] bg-white/60 p-8 flex flex-col" data-testid={`review-${t.id}`}>
                      {t.rating > 0 && <div className="mb-5"><Diamonds count={t.rating} /></div>}
                      <blockquote className="font-serif italic text-xl md:text-2xl leading-snug text-[#1A1A1A] grow">
                        "{t.text}"
                      </blockquote>
                      <figcaption className="mt-6 flex items-center gap-4">
                        {t.photo && <img src={resolveImage(t.photo)} alt={t.name} className="w-11 h-11 object-cover" loading="lazy" />}
                        <div>
                          <p className="font-mono text-[0.7rem] tracking-[0.18em] uppercase font-bold">{t.name}</p>
                          {(t.project || t.date) && (
                            <p className="text-xs text-[#A3A09B] mt-1">{[t.project, t.date].filter(Boolean).join(" — ")}</p>
                          )}
                        </div>
                      </figcaption>
                    </figure>
                  </StaggerItem>
                ))}
              </Stagger>
            </>
          ) : (
            <FadeUp>
              <div className="max-w-2xl" data-testid="reviews-empty">
                <p className="font-serif italic text-2xl md:text-3xl text-[#A3A09B] leading-snug">
                  "Client stories are on their way — our first homeowners are still unpacking. In the meantime,
                  walk our work and judge for yourself."
                </p>
                <Link to="/our-work" data-testid="reviews-work-link" className="mt-6 inline-flex items-center gap-3 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold link-underline w-fit not-italic">
                  See Our Work <ArrowRight size={14} />
                </Link>
              </div>
            </FadeUp>
          )}

          <FadeUp className="mt-20 border-t border-[rgba(26,26,26,0.12)] pt-14">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-10">
              <div>
                <p className="eyebrow text-[#8C6A4F] mb-4">Built With Us?</p>
                <h2 className="font-serif text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  Your story belongs here too.
                </h2>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/testimonial"
                  data-testid="share-story-btn"
                  className="btn-sweep bg-[#1A1A1A] text-[#F7F5F2] font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold px-10 py-5 inline-flex items-center gap-3"
                >
                  Share Your Experience <ArrowRight size={14} />
                </Link>
                {settings.google_review_url && (
                  <a
                    href={settings.google_review_url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="reviews-google-link"
                    className="inline-flex items-center gap-3 border border-[#1A1A1A] px-10 py-5 font-mono text-[0.7rem] tracking-[0.2em] uppercase font-bold hover:bg-[#1A1A1A] hover:text-[#F7F5F2] transition-colors duration-300"
                  >
                    Review Us On Google <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
};

export default Reviews;
